import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EventModel } from '@app/events/shared/event.model';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { ToastService } from '@app/shared/toast/toast.service';
import { AlertTriangle, Armchair, Baby, LayoutGrid, List, Plus, Trash2, Users } from 'lucide-angular';
import { LucideAngularModule } from 'lucide-angular';
import { TablePlanCanvasComponent } from './canvas/table-plan-canvas.component';
import { TablePlanService } from './table-plan.service';
import {
  TablePlan,
  TablePlanGuest,
  TablePlanPreset,
  TablePlanResponse,
  TablePlanSummary,
  TablePlanTable,
  TablePlanVenueTemplate,
} from './table-plan.model';
import { DEFAULT_FURNITURE, FurnitureSettings, TileType, footprint, sideCountsOf, tooClose, trestleCount } from './canvas/canvas-geometry';
import { boxFitsInRoom, outlineForVenue } from './canvas/room-outlines/rooms';
import { PreventNumberScrollDirective } from '@app/shared/directives/prevent-number-scroll.directive';

export type TablePlanView = 'canvas' | 'panel' | 'both';

const VIEW_STORAGE_KEY = 'tvg.table-plan.view';

/**
 * TVG-98: Panel-first Table Plan tab on the event record.
 *
 * V1 ships the list-based "panel" interface only. The canvas (click a seat,
 * drag tables around) lands later once Richard signs off the mock-up. The
 * panel and canvas operate on the same plan and the panel alone is enough
 * for a couple who would rather work from a list.
 */
@Component({
  selector: 'app-table-plan',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, LucideAngularModule, TablePlanCanvasComponent, PreventNumberScrollDirective],
  templateUrl: './table-plan.component.html',
})
export class TablePlanComponent implements OnInit {
  @Input({ required: true }) public event!: EventModel;

  /**
   * TVG-142: bubbles up when a guest tile on the canvas is clicked, so the
   * event view can switch to the Guests tab and open that guest's record.
   */
  @Output() public openGuest = new EventEmitter<number>();

  protected readonly PlusIcon = Plus;
  protected readonly TrashIcon = Trash2;
  protected readonly AlertIcon = AlertTriangle;
  protected readonly ChairIcon = Armchair;
  protected readonly UsersIcon = Users;
  protected readonly BabyIcon = Baby;
  protected readonly CanvasIcon = LayoutGrid;
  protected readonly PanelIcon = List;

  // TVG-103: view-switcher state. Only meaningful when the canvas toggle is on.
  protected view: TablePlanView = 'both';
  protected readonly viewOptions: ReadonlyArray<{ value: TablePlanView; label: string }> = [
    { value: 'canvas', label: 'Canvas' },
    { value: 'both', label: 'Both' },
    { value: 'panel', label: 'Panel' },
  ];

  protected loading = false;
  protected saving = false;
  protected loadError: string | null = null;

  protected plan: TablePlan | null = null;
  protected summary: TablePlanSummary | null = null;
  protected guests: TablePlanGuest[] = [];
  protected presets: TablePlanPreset[] = [];
  // TVG-189: saved per-venue templates (arrange-then-save arrangements).
  protected venueTemplates: TablePlanVenueTemplate[] = [];
  protected selectedVenueTemplateId: number | null = null;

  protected newTableLabel = '';
  protected newTableSeatCount = 10;

  protected selectedPresetId: number | null = null;

  protected expandedTableIds = new Set<number>();

  // TVG-101: Quick-setup wizard local state.
  protected quickSetupStep: 1 | 2 = 1;
  protected quickSetupTopSize: number | null = null;
  protected quickSetupTargetSize: 8 | 10 | 12 = 10;
  protected readonly quickSetupTargetChoices: ReadonlyArray<8 | 10 | 12> = [8, 10, 12];
  protected readonly topSizeExamples: ReadonlyArray<{ size: number; description: string }> = [
    { size: 2, description: 'couple only' },
    { size: 4, description: 'couple plus parents' },
    { size: 6, description: 'couple plus parents plus best man and maid of honour' },
    { size: 8, description: 'couple plus wedding party' },
    { size: 10, description: 'larger top table' },
  ];

  constructor(
    private tablePlanService: TablePlanService,
    private toastService: ToastService,
    private featureToggleService: FeatureToggleService,
  ) {}

  ngOnInit(): void {
    this.restoreView();
    this.load();
  }

  /**
   * TVG-103: True when the new canvas view is enabled. When false the panel
   * is the only view and the view-switcher is hidden, matching pre-canvas
   * behaviour.
   */
  protected get canvasEnabled(): boolean {
    return this.featureToggleService.isEnabled('table-plan-canvas');
  }

  /**
   * TVG-144 / TVG-197: the live editable canvas. The per-venue layout templates
   * now live in the canvas' own left editor pane (previewed on the canvas and
   * applied in place) rather than a separate gallery page, so the canvas shows
   * whenever the canvas view is active — with or without a layout chosen yet.
   */
  protected get showCanvas(): boolean {
    return this.canvasEnabled && (this.view === 'canvas' || this.view === 'both');
  }

  protected get showPanel(): boolean {
    return !this.canvasEnabled || this.view === 'panel' || this.view === 'both';
  }

  /** Key of the layout template this plan was opened from, or null. */
  /** TVG-373: the furniture settings the canvas draws with. */
  protected furniture: FurnitureSettings = DEFAULT_FURNITURE;

  /**
   * TVG-373: the selected saved template's seat total when the plan has gone
   * past it — a gentle note, never a block.
   */
  protected get templateGuidanceExceeded(): number | null {
    const key = this.chosenLayoutKey;
    if (!key?.startsWith('venue-template:') || !this.summary) {
      return null;
    }
    const template = this.venueTemplates.find((t) => `venue-template:${t.id}` === key);
    return template && this.summary.seat_count > template.seat_total ? template.seat_total : null;
  }

  protected get chosenLayoutKey(): string | null {
    return this.plan?.layout_template_key ?? null;
  }

  // TVG-189 ---------------------------------------------------------------

  /**
   * TVG-189: with the canvas on, saved templates live inside the canvas' left
   * editor pane (see `showCanvas`), so the standalone card is only used in the
   * list-only panel view — where there is no left pane to host them. Hidden
   * until the plan has loaded.
   */
  protected get showVenueTemplatesCard(): boolean {
    if (this.loading || this.loadError || !this.plan) {
      return false;
    }
    return this.showPanel && !this.showCanvas;
  }

  /**
   * TVG-189: snapshot the current arrangement as a reusable per-venue template.
   * The server reads the tables straight off the plan, so we only prompt for a
   * name.
   */
  protected saveAsTemplate(name?: string): void {
    if (!this.hasTables) {
      this.toastService.error('Add at least one table before saving a template.');
      return;
    }
    // The canvas passes the name typed into its in-pane form. The list-only
    // panel card has no such field, so it falls back to a browser prompt.
    const finalName = (name ?? prompt('Name this template') ?? '').trim();
    if (!finalName) {
      return;
    }
    this.saving = true;
    this.tablePlanService.saveTemplate(this.event.id, finalName).subscribe({
      next: (res) => {
        this.absorb(res);
        this.toastService.success('Template saved for this venue.');
      },
      error: () => this.handleError('Failed to save the template.'),
    });
  }

  /**
   * TVG-189: load a saved per-venue template onto the plan. Applying replaces
   * the current tables but the plan stays fully editable afterwards.
   */
  protected applyVenueTemplate(template?: TablePlanVenueTemplate): void {
    const templateId = template?.id ?? this.selectedVenueTemplateId;
    if (templateId === null) {
      return;
    }
    if (this.hasTables && !confirm('Loading this template replaces the current tables. Continue?')) {
      return;
    }
    this.saving = true;
    this.tablePlanService.applyTemplate(this.event.id, templateId).subscribe({
      next: (res) => this.absorb(res),
      error: () => this.handleError('Failed to load that template.'),
    });
  }

  protected deleteVenueTemplate(template: TablePlanVenueTemplate): void {
    if (!confirm(`Delete the saved template "${template.name}"?`)) {
      return;
    }
    this.saving = true;
    this.tablePlanService.deleteTemplate(this.event.id, template.id).subscribe({
      next: (res) => this.absorb(res),
      error: () => this.handleError('Failed to delete the template.'),
    });
  }

  /**
   * TVG-227: rename an existing saved template. The canvas emits the trimmed
   * name from its inline field; only the name changes, the stored layout is
   * left untouched.
   */
  protected renameVenueTemplate(payload: { template: TablePlanVenueTemplate; name: string }): void {
    const name = payload.name.trim();
    if (!name || name === payload.template.name) {
      return;
    }
    this.saving = true;
    this.tablePlanService.updateTemplate(this.event.id, payload.template.id, { name }).subscribe({
      next: (res) => {
        this.absorb(res);
        this.toastService.success('Template renamed.');
      },
      error: () => this.handleError('Failed to rename the template.'),
    });
  }

  /**
   * TVG-227: the list-only panel has no inline field, so it prompts for the new
   * name before delegating to the shared rename handler.
   */
  protected promptRenameVenueTemplate(template: TablePlanVenueTemplate): void {
    const name = (prompt('Rename this template', template.name) ?? '').trim();
    if (!name || name === template.name) {
      return;
    }
    this.renameVenueTemplate({ template, name });
  }

  /**
   * TVG-227: overwrite a saved template's stored layout with the current
   * arrangement — "save my edits back to the same template". Confirmed because
   * it replaces the template's saved geometry.
   */
  protected updateVenueTemplateLayout(template: TablePlanVenueTemplate): void {
    if (!this.hasTables) {
      this.toastService.error('Add at least one table before saving it to a template.');
      return;
    }
    if (!confirm(`Overwrite "${template.name}" with the current layout?`)) {
      return;
    }
    this.saving = true;
    this.tablePlanService.updateTemplate(this.event.id, template.id, { sync_layout: true }).subscribe({
      next: (res) => {
        this.absorb(res);
        this.toastService.success('Template updated.');
      },
      error: () => this.handleError('Failed to update the template.'),
    });
  }

  protected setView(value: TablePlanView): void {
    this.view = value;
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, value);
    } catch {
      // Ignore quota / storage-disabled errors; view falls back to default.
    }
  }

  /**
   * TVG-103: Called by the canvas after every optimistic mutation it applies
   * to the shared plan reference. We deliberately do NOT re-fetch from the
   * server here — fetching toggled `loading=true` which un-mounted the
   * canvas on every drag-and-drop (scroll position, selection state and
   * viewport all reset). Instead the canvas owns optimistic plan mutations
   * and we recompute the panel summary client-side so the stats stay in
   * sync without a network round-trip.
   */
  protected onCanvasChange(): void {
    this.recomputeSummary();
  }

  protected onCanvasError(message: string): void {
    this.toastService.error(message);
  }

  /**
   * Mirror of TablePlanService::countCanvasAdvisories + summary computation
   * for the canvas-driven optimistic-update flow. The figures match what the
   * server returns on a fresh GET so the panel keeps showing accurate
   * counters without re-fetching after every drag.
   */
  private recomputeSummary(): void {
    if (!this.plan) {
      return;
    }
    const tables = this.plan.tables;
    let seatCount = 0;
    let assignedCount = 0;
    let highChairCount = 0;
    for (const t of tables) {
      seatCount += t.seats.length;
      for (const s of t.seats) {
        if (s.event_guest_id !== null) {
          assignedCount++;
          // TVG-142: high chair is a guest need, so the counter follows the
          // seated guest's own flag rather than a per-seat toggle.
          const guest = this.guests.find((g) => g.id === s.event_guest_id);
          if (guest?.requires_high_chair) {
            highChairCount++;
          }
        }
      }
    }
    const guestCount = this.guests.length;
    const venueCapacity = this.summary?.venue_capacity ?? null;

    // Placed tables only — unplaced tables (position 0,0) sit in the panel's
    // unplaced bucket and don't count toward outside/overlap advisories.
    const outline = outlineForVenue(this.event?.venue?.name ?? null);
    let outsideRoom = 0;
    // TVG-373: a table is flagged when it, or its chairs, reach into another
    // table's walking space.
    const boxes: Array<{ foot: { x1: number; y1: number; x2: number; y2: number }; space: { x1: number; y1: number; x2: number; y2: number }; id: number }> = [];
    for (const t of tables) {
      if (t.position_x === 0 && t.position_y === 0) {
        continue;
      }
      const type = t.tile_type as TileType;
      const n = trestleCount(type, t.trestle_count);
      const counts = sideCountsOf(t.seats);
      const foot = footprint(type, n, t.position_x, t.position_y, t.rotation, counts);
      const space = footprint(type, n, t.position_x, t.position_y, t.rotation, counts, this.furniture.walkingSpace);
      if (outline && !boxFitsInRoom(foot, outline)) {
        outsideRoom++;
      }
      boxes.push({ foot, space, id: t.id });
    }
    const flagged = new Set<number>();
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i];
        const b = boxes[j];
        if (tooClose(a.foot, a.space, b.foot, b.space)) {
          flagged.add(a.id);
          flagged.add(b.id);
        }
      }
    }

    // TVG-132: the chair cap is a constant (the event's day guest count) so we
    // carry it across optimistic updates and recompute the pool from the live
    // chair count, keeping the pool counter accurate without a re-fetch.
    const chairCap = this.summary?.chair_cap ?? null;
    const chairPoolEnforced = this.summary?.chair_pool_enforced ?? false;
    const chairPool =
      chairPoolEnforced && chairCap !== null ? Math.max(0, chairCap - seatCount) : null;

    this.summary = {
      table_count: tables.length,
      seat_count: seatCount,
      assigned_count: assignedCount,
      unassigned_count: seatCount - assignedCount,
      high_chair_count: highChairCount,
      guest_count: guestCount,
      guests_without_seat: Math.max(0, guestCount - assignedCount),
      venue_capacity: venueCapacity,
      over_venue_capacity: venueCapacity !== null && seatCount > venueCapacity,
      tables_outside_room: outsideRoom,
      tables_overlapping: flagged.size,
      chair_cap: chairCap,
      chair_pool: chairPool,
      chair_pool_enforced: chairPoolEnforced,
    };
  }

  private restoreView(): void {
    try {
      const stored = localStorage.getItem(VIEW_STORAGE_KEY) as TablePlanView | null;
      if (stored === 'canvas' || stored === 'panel' || stored === 'both') {
        this.view = stored;
      }
    } catch {
      // localStorage may be unavailable (private mode, server-render); default stays.
    }
  }

  protected toggleExpanded(tableId: number): void {
    if (this.expandedTableIds.has(tableId)) {
      this.expandedTableIds.delete(tableId);
    } else {
      this.expandedTableIds.add(tableId);
    }
  }

  protected isExpanded(tableId: number): boolean {
    return this.expandedTableIds.has(tableId);
  }

  protected get hasTables(): boolean {
    return (this.plan?.tables.length ?? 0) > 0;
  }

  /**
   * Roster of guests not yet seated, plus the guest currently on this seat if
   * any. Used to populate the per-seat dropdown so a guest never appears in
   * two pickers at once.
   */
  protected availableGuestsForSeat(currentGuestId: number | null): TablePlanGuest[] {
    const seated = new Set<number>();
    this.plan?.tables.forEach((table) =>
      table.seats.forEach((seat) => {
        if (seat.event_guest_id !== null) {
          seated.add(seat.event_guest_id);
        }
      }),
    );
    return this.guests.filter((g) => !seated.has(g.id) || g.id === currentGuestId);
  }

  // TVG-101 ---------------------------------------------------------------

  protected get totalGuestCount(): number {
    return this.summary?.guest_count ?? this.guests.length;
  }

  protected get quickSetupRemainder(): number {
    const top = this.normalisedTopSize();
    if (top === null) {
      return 0;
    }
    return Math.max(0, this.totalGuestCount - top);
  }

  protected get quickSetupCanAdvance(): boolean {
    const top = this.normalisedTopSize();
    return top !== null && top <= this.totalGuestCount;
  }

  protected get quickSetupPreview(): { bodyTables: { size: number; count: number }[]; total: number } | null {
    const top = this.normalisedTopSize();
    if (top === null) {
      return null;
    }
    const remainder = Math.max(0, this.totalGuestCount - top);
    const sizes = this.computeBodyTableSizes(remainder, this.quickSetupTargetSize);
    const grouped = new Map<number, number>();
    sizes.forEach((s) => grouped.set(s, (grouped.get(s) ?? 0) + 1));
    const bodyTables = Array.from(grouped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([size, count]) => ({ size, count }));
    return { bodyTables, total: top + sizes.reduce((acc, s) => acc + s, 0) };
  }

  protected goToStepTwo(): void {
    if (!this.quickSetupCanAdvance) {
      this.toastService.error('Top table size must not exceed the guest count.');
      return;
    }
    this.quickSetupStep = 2;
  }

  protected backToStepOne(): void {
    this.quickSetupStep = 1;
  }

  protected applyQuickSetup(): void {
    const top = this.normalisedTopSize();
    if (top === null) {
      return;
    }
    if (this.hasTables && !confirm('This will replace the current tables. Continue?')) {
      return;
    }
    this.saving = true;
    this.tablePlanService
      .quickSetup(this.event.id, { top_table_size: top, target_body_size: this.quickSetupTargetSize })
      .subscribe({
        next: (res) => {
          this.absorb(res);
          this.quickSetupStep = 1;
          this.quickSetupTopSize = null;
        },
        error: () => this.handleError('Failed to apply quick setup.'),
      });
  }

  private normalisedTopSize(): number | null {
    const raw = this.quickSetupTopSize;
    if (raw === null || raw === undefined) {
      return null;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || Math.floor(n) !== n) {
      return null;
    }
    return n;
  }

  /**
   * TVG-101: Mirror of the backend distribution algorithm so the wizard can
   * show a live preview before the user commits. Keep in sync with
   * TablePlanService::computeQuickSetupLayout.
   */
  private computeBodyTableSizes(remainder: number, target: 8 | 10 | 12): number[] {
    if (remainder <= 0) {
      return [];
    }
    const minSize = 4;
    const maxSize = 12;
    let best: { n: number; small: number; big: number; score: [number, number, number] } | null = null;

    for (let n = 1; n <= remainder; n++) {
      const small = Math.floor(remainder / n);
      const big = remainder % n === 0 ? small : small + 1;
      const feasible = small >= minSize && big <= maxSize;
      const avg = remainder / n;
      const distance = Math.abs(avg - target);
      const score: [number, number, number] = [feasible ? 0 : 1, distance, n];

      if (best === null || this.compareScores(score, best.score) < 0) {
        best = { n, small, big, score };
      }

      if (small < minSize && n > 1) {
        break;
      }
    }

    if (best === null) {
      return [remainder];
    }
    const { n, small, big } = best;
    const bigCount = remainder - n * small;
    const smallCount = n - bigCount;
    return [
      ...Array.from({ length: smallCount }, () => small),
      ...Array.from({ length: bigCount }, () => big),
    ];
  }

  private compareScores(a: [number, number, number], b: [number, number, number]): number {
    return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
  }

  // -----------------------------------------------------------------------

  protected applyPreset(): void {
    if (this.selectedPresetId === null) {
      return;
    }
    const presetId = this.selectedPresetId;
    const hasExisting = this.hasTables;
    if (hasExisting && !confirm('Applying a preset will replace the current tables. Continue?')) {
      return;
    }
    this.saving = true;
    this.tablePlanService.applyPreset(this.event.id, presetId).subscribe({
      next: (res) => this.absorb(res),
      error: () => this.handleError('Failed to apply preset.'),
    });
  }

  protected addTable(): void {
    const label = this.newTableLabel.trim();
    if (!label) {
      this.toastService.error('Table label is required.');
      return;
    }
    const seatCount = Math.max(0, Math.min(64, Number(this.newTableSeatCount) || 0));
    this.saving = true;
    this.tablePlanService.addTable(this.event.id, { label, seat_count: seatCount }).subscribe({
      next: (res) => {
        this.absorb(res);
        this.newTableLabel = '';
        this.newTableSeatCount = 10;
      },
      error: () => this.handleError('Failed to add table.'),
    });
  }

  protected renameTable(table: TablePlanTable, label: string): void {
    const trimmed = label.trim();
    if (!trimmed || trimmed === table.label) {
      return;
    }
    this.saving = true;
    this.tablePlanService.updateTable(this.event.id, table.id, { label: trimmed }).subscribe({
      next: (res) => this.absorb(res),
      error: () => this.handleError('Failed to rename table.'),
    });
  }

  protected updateSeatCount(table: TablePlanTable, raw: string | number): void {
    const value = Math.max(0, Math.min(64, Number(raw) || 0));
    if (value === table.seat_count) {
      return;
    }
    this.saving = true;
    this.tablePlanService.updateTable(this.event.id, table.id, { seat_count: value }).subscribe({
      next: (res) => this.absorb(res),
      error: () => this.handleError('Failed to update seat count.'),
    });
  }

  protected deleteTable(table: TablePlanTable): void {
    if (!confirm(`Delete "${table.label}" and clear its seat assignments?`)) {
      return;
    }
    this.saving = true;
    this.tablePlanService.deleteTable(this.event.id, table.id).subscribe({
      next: (res) => {
        this.expandedTableIds.delete(table.id);
        this.absorb(res);
      },
      error: () => this.handleError('Failed to delete table.'),
    });
  }

  protected assignGuest(seatId: number, guestId: number | null): void {
    this.saving = true;
    this.tablePlanService.updateSeat(this.event.id, seatId, { event_guest_id: guestId }).subscribe({
      next: (res) => this.absorb(res),
      error: () => this.handleError('Failed to assign guest.'),
    });
  }

  private load(): void {
    if (!this.event?.id) {
      return;
    }
    this.loading = true;
    this.loadError = null;
    this.tablePlanService.load(this.event.id).subscribe({
      next: (res) => {
        this.absorb(res);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadError = 'Failed to load table plan.';
      },
    });
  }

  private absorb(res: TablePlanResponse): void {
    this.plan = res.data;
    this.summary = res.meta.summary;
    this.guests = res.meta.guest_roster ?? [];
    this.presets = res.meta.presets ?? [];
    this.venueTemplates = res.meta.venue_templates ?? [];
    // TVG-373: seats per trestle side and walking space, from the settings.
    this.furniture = res.meta.furniture
      ? { seatsPerSide: res.meta.furniture.seats_per_side, walkingSpace: res.meta.furniture.walking_space }
      : DEFAULT_FURNITURE;
    if (!this.venueTemplates.some((t) => t.id === this.selectedVenueTemplateId)) {
      this.selectedVenueTemplateId = null;
    }
    this.saving = false;
  }

  private handleError(message: string): void {
    this.saving = false;
    this.toastService.error(message);
  }
}
